/**
 * theme.js — all client behaviour for the theme.
 *
 * Written as one small IIFE with no dependencies. Everything in here is an
 * enhancement over markup that already works: the cart icon is a real link to
 * /cart, the search form is a real GET to /search, and every section renders
 * server-side. If this file fails to load, the store still sells.
 *
 * Contents
 *   1. Utilities (money formatting, focus handling, scroll lock)
 *   2. Theme toggle
 *   3. Mobile navigation
 *   4. Cart drawer + Cart AJAX API
 *   5. Predictive search
 *   6. Scroll reveal
 *   7. Sticky buy bar
 *   8. Header condense
 */

(function () {
  'use strict'

  var root = document.documentElement

  /* ───────────────────────────── 1. Utilities ─────────────────────────── */

  function $(selector, scope) {
    return (scope || document).querySelector(selector)
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector))
  }

  /**
   * Formats cents as currency.
   *
   * The Cart AJAX API returns integer cents, never a formatted string, so
   * something has to do this. Intl is used rather than reimplementing
   * Shopify's money_format templating, which has a dozen variants and would
   * drift from the shop's real setting. Currency comes from the global
   * Shopify sets in content_for_header.
   */
  function money(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
      }).format(cents / 100)
    } catch (e) {
      return '$' + (cents / 100).toFixed(2)
    }
  }

  /**
   * Formats a decimal amount string as currency.
   *
   * Shopify is not consistent about this across APIs and it is a genuine
   * trap: /cart.js returns integer cents (60000), while
   * /search/suggest.json returns a decimal string ("600.00"). Passing the
   * latter to money() divides by 100 a second time and quietly renders
   * $600.00 as $6.00 — wrong in the one place a customer is deciding
   * whether they can afford it.
   */
  function moneyFromAmount(amount) {
    var value = parseFloat(amount)
    if (isNaN(value)) return ''
    return money(Math.round(value * 100))
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  /* Scroll lock. overflow:hidden rather than position:fixed, so closing the
     drawer does not throw the reader back to the top of the page. */
  var lockCount = 0

  function lockScroll() {
    lockCount += 1
    root.classList.add('scroll-locked')
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1)
    if (lockCount === 0) root.classList.remove('scroll-locked')
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

  /**
   * Keeps Tab inside an open dialog.
   *
   * Without this, tabbing out of the cart drawer lands silently on the page
   * behind it, which for a screen reader user means the dialog has
   * effectively vanished while still covering the screen.
   */
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return
    var items = $$(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null
    })
    if (items.length === 0) return
    var first = items[0]
    var last = items[items.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  /* The light/dark toggle was removed with its button — the stylesheet still
     answers the visitor own OS setting through prefers-color-scheme, which
     needs no JavaScript at all. */

  /* ───────────────────────── 3. Mobile navigation ─────────────────────── */

  function closeNav() {
    var button = $('[data-nav-toggle]')
    var panel = document.getElementById('mobile-nav')
    if (!button || !panel) return
    button.setAttribute('aria-expanded', 'false')
    button.textContent = button.getAttribute('data-label-open') || 'Menu'
    panel.hidden = true
  }

  /* ───────────────────── 4. Cart drawer + Cart AJAX ───────────────────── */

  var drawer = $('[data-cart-drawer]')
  var overlay = $('[data-overlay]')
  var cartBody = $('[data-cart-body]')
  var cartFoot = $('[data-cart-foot]')
  var cartTotal = $('[data-cart-total]')
  var toastEl = $('[data-toast]')
  var lastFocus = null
  var toastTimer = null

  function toast(message) {
    if (!toastEl) return
    toastEl.textContent = message
    toastEl.setAttribute('data-open', 'true')
    clearTimeout(toastTimer)
    toastTimer = setTimeout(function () {
      toastEl.setAttribute('data-open', 'false')
    }, 3200)
  }

  function openDrawer() {
    if (!drawer) return
    lastFocus = document.activeElement
    drawer.setAttribute('data-open', 'true')
    drawer.setAttribute('aria-hidden', 'false')
    if (overlay) {
      overlay.setAttribute('data-open', 'true')
      overlay.setAttribute('aria-hidden', 'false')
    }
    lockScroll()
    var close = $('[data-drawer-close]', drawer)
    if (close) close.focus()
  }

  function closeDrawer() {
    if (!drawer || drawer.getAttribute('data-open') !== 'true') return
    drawer.setAttribute('data-open', 'false')
    drawer.setAttribute('aria-hidden', 'true')
    if (overlay) {
      overlay.setAttribute('data-open', 'false')
      overlay.setAttribute('aria-hidden', 'true')
    }
    unlockScroll()
    if (lastFocus && lastFocus.focus) lastFocus.focus()
    lastFocus = null
  }

  function setCartBusy(busy) {
    if (cartBody) cartBody.setAttribute('aria-busy', busy ? 'true' : 'false')
  }

  /**
   * Rebuilds the drawer contents from a cart object.
   *
   * Deliberately mirrors snippets/cart-drawer.liquid. Two renderers for one
   * structure is a maintenance cost, but the alternative — fetching a
   * rendered section over the network for every quantity tap — is slower and
   * fails worse offline.
   */
  function renderCart(cart) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count
      el.hidden = cart.item_count === 0
    })

    if (cartTotal) cartTotal.textContent = money(cart.total_price)
    if (cartFoot) cartFoot.hidden = cart.item_count === 0
    if (!cartBody) return

    if (cart.item_count === 0) {
      cartBody.innerHTML =
        '<div class="drawer__empty">' +
        '<p class="lede">Nothing here yet.</p>' +
        '<p class="small muted" style="margin-top: 0.75rem;">Deposits and hourly work can be booked directly. Anything larger starts with an enquiry.</p>' +
        '</div>'
      return
    }

    cartBody.innerHTML = cart.items
      .map(function (item, index) {
        var meta = ''
        if (item.variant_title && item.variant_title.indexOf('Default') === -1) {
          meta += '<p class="cart-line__meta">' + escapeHtml(item.variant_title) + '</p>'
        }
        Object.keys(item.properties || {}).forEach(function (key) {
          var value = item.properties[key]
          if (!value || key.charAt(0) === '_') return
          meta += '<p class="cart-line__meta">' + escapeHtml(key) + ': ' + escapeHtml(value) + '</p>'
        })

        var title = escapeHtml(item.product_title)
        return (
          '<div class="cart-line" data-line="' + (index + 1) + '">' +
          '<div class="cart-line__top"><div>' +
          '<p class="cart-line__title"><a href="' + item.url + '">' + title + '</a></p>' +
          meta +
          '</div><p class="cart-line__price">' + money(item.final_line_price) + '</p></div>' +
          '<div class="cart-line__controls"><div class="qty">' +
          '<button type="button" class="qty__btn" data-qty-down aria-label="Decrease quantity of ' + title + '">&minus;</button>' +
          '<span class="qty__value" data-qty-value>' + item.quantity + '</span>' +
          '<button type="button" class="qty__btn" data-qty-up aria-label="Increase quantity of ' + title + '">+</button>' +
          '</div>' +
          '<button type="button" class="cart-line__remove" data-line-remove>Remove</button>' +
          '</div></div>'
        )
      })
      .join('')
  }

  function changeLine(line, quantity) {
    setCartBusy(true)
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update failed')
        return response.json()
      })
      .then(function (cart) {
        renderCart(cart)
      })
      .catch(function () {
        toast('That did not update. Please try again.')
      })
      .then(function () {
        setCartBusy(false)
      })
  }

  /* Add to cart, intercepting the product form so the page does not navigate
     away mid-decision. Falls back to a normal submit on failure. */
  function handleProductSubmit(form) {
    var button = form.querySelector('[type="submit"]')
    var original = button ? button.textContent : ''
    if (button) {
      button.disabled = true
      button.textContent = 'Adding…'
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (e) { throw new Error(e.description || 'Could not add') })
        return response.json()
      })
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json() })
      })
      .then(function (cart) {
        renderCart(cart)
        openDrawer()
        toast('Added to your order.')
      })
      .catch(function (error) {
        toast(error.message || 'That could not be added.')
      })
      .then(function () {
        if (button) {
          button.disabled = false
          button.textContent = original
        }
      })
  }

  /* ───────────────────────── 5. Predictive search ─────────────────────── */

  var searchPanel = $('[data-search-panel]')
  var searchInput = $('[data-search-input]')
  var searchResults = $('[data-search-results]')
  var searchTimer = null
  var searchAbort = null
  var activeIndex = -1

  function openSearch() {
    if (!searchPanel) return
    lastFocus = document.activeElement
    searchPanel.setAttribute('data-open', 'true')
    searchPanel.setAttribute('aria-hidden', 'false')
    if (overlay) {
      overlay.setAttribute('data-open', 'true')
      overlay.setAttribute('aria-hidden', 'false')
    }
    $$('[data-search-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true') })
    lockScroll()
    if (searchInput) searchInput.focus()
  }

  function closeSearch() {
    if (!searchPanel || searchPanel.getAttribute('data-open') !== 'true') return
    searchPanel.setAttribute('data-open', 'false')
    searchPanel.setAttribute('aria-hidden', 'true')
    if (overlay && (!drawer || drawer.getAttribute('data-open') !== 'true')) {
      overlay.setAttribute('data-open', 'false')
      overlay.setAttribute('aria-hidden', 'true')
    }
    $$('[data-search-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false') })
    if (searchInput) searchInput.setAttribute('aria-expanded', 'false')
    unlockScroll()
    if (lastFocus && lastFocus.focus) lastFocus.focus()
    lastFocus = null
  }

  function renderSearch(data, term) {
    if (!searchResults) return
    var products = (data.resources.results.products || []).filter(function (p) { return p.available !== false })
    var pages = data.resources.results.pages || []
    activeIndex = -1

    if (products.length === 0 && pages.length === 0) {
      searchResults.innerHTML =
        '<p class="search-empty">Nothing matches &ldquo;' + escapeHtml(term) + '&rdquo;. ' +
        'This is a small site &mdash; try <a href="/pages/services">Services</a> or ' +
        '<a href="/pages/contact">send an enquiry</a> and ask directly.</p>'
      if (searchInput) searchInput.setAttribute('aria-expanded', 'false')
      return
    }

    var html = ''

    if (products.length > 0) {
      html += '<h2 class="search-results__heading">Services</h2>'
      html += products
        .map(function (p, i) {
          var image = p.featured_image && p.featured_image.url
            ? '<img src="' + p.featured_image.url + '" alt="" loading="lazy">'
            : escapeHtml(p.title.charAt(0))
          return (
            '<a class="search-hit" id="hit-' + i + '" role="option" aria-selected="false" href="' + p.url + '">' +
            '<span class="search-hit__media" aria-hidden="true">' + image + '</span>' +
            '<span><span class="search-hit__title">' + escapeHtml(p.title) + '</span>' +
            '<span class="search-hit__meta">' + escapeHtml(p.price ? moneyFromAmount(p.price) : '') + '</span></span>' +
            '</a>'
          )
        })
        .join('')
    }

    if (pages.length > 0) {
      html += '<h2 class="search-results__heading" style="margin-top: 1.25rem;">Pages</h2>'
      html += pages
        .map(function (page, i) {
          return (
            '<a class="search-hit" id="hit-' + (products.length + i) + '" role="option" aria-selected="false" href="' + page.url + '">' +
            '<span class="search-hit__media" aria-hidden="true">#</span>' +
            '<span><span class="search-hit__title">' + escapeHtml(page.title) + '</span></span>' +
            '</a>'
          )
        })
        .join('')
    }

    searchResults.innerHTML = html
    if (searchInput) searchInput.setAttribute('aria-expanded', 'true')
  }

  function runSearch(term) {
    if (!term || term.length < 2) {
      if (searchResults) searchResults.innerHTML = ''
      if (searchInput) searchInput.setAttribute('aria-expanded', 'false')
      return
    }

    if (searchAbort) searchAbort.abort()
    searchAbort = typeof AbortController === 'function' ? new AbortController() : null

    var url =
      '/search/suggest.json?q=' + encodeURIComponent(term) +
      '&resources[type]=product,page&resources[limit]=6'

    fetch(url, {
      headers: { Accept: 'application/json' },
      signal: searchAbort ? searchAbort.signal : undefined,
    })
      .then(function (response) { return response.json() })
      .then(function (data) { renderSearch(data, term) })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return
        if (searchResults) {
          searchResults.innerHTML = '<p class="search-empty">Search is unavailable right now. Press Enter to try the full results page.</p>'
        }
      })
  }

  function moveActive(delta) {
    var hits = $$('.search-hit', searchResults)
    if (hits.length === 0) return
    if (activeIndex >= 0 && hits[activeIndex]) hits[activeIndex].setAttribute('aria-selected', 'false')
    activeIndex = (activeIndex + delta + hits.length) % hits.length
    var current = hits[activeIndex]
    current.setAttribute('aria-selected', 'true')
    current.scrollIntoView({ block: 'nearest' })
    if (searchInput) searchInput.setAttribute('aria-activedescendant', current.id)
  }

  /* ───────────────────────────── 6. Scroll reveal ─────────────────────── */

  function initReveal() {
    var targets = $$('[data-reveal]')
    if (targets.length === 0) return

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver !== 'function') {
      targets.forEach(function (el) { el.setAttribute('data-reveal', 'in') })
      return
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return
          entry.target.setAttribute('data-reveal', 'in')
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    )

    targets.forEach(function (el) { observer.observe(el) })
  }

  /* ───────────────────────────── 7. Carousel ──────────────────────────── */

  /**
   * Enhances a scroll-snap track with arrows and a progress bar.
   *
   * The track scrolls natively, so this never moves content itself beyond
   * calling scrollBy — there is no transform, no index state and no way for
   * the JavaScript's idea of position to drift from what is on screen. That
   * is the main reason not to write a slider.
   */
  function initCarousels() {
    $$('[data-carousel]').forEach(function (carousel) {
      if (carousel.dataset.carouselReady === 'true') return
      carousel.dataset.carouselReady = 'true'

      var track = $('[data-carousel-track]', carousel)
      var prev = $('[data-carousel-prev]', carousel)
      var next = $('[data-carousel-next]', carousel)
      var bar = $('[data-carousel-bar]', carousel)
      if (!track) return

      function page() {
        var first = track.firstElementChild
        /* Scroll by one card plus the gap, so a card never ends up half cut
           off at the edge. */
        if (!first) return track.clientWidth
        var styles = window.getComputedStyle(track)
        var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0
        return first.getBoundingClientRect().width + gap
      }

      function update() {
        var max = track.scrollWidth - track.clientWidth
        var atStart = track.scrollLeft <= 1
        var atEnd = track.scrollLeft >= max - 1

        if (prev) prev.disabled = atStart
        if (next) next.disabled = atEnd

        /* Hide the controls entirely when everything already fits, rather
           than showing two permanently dead buttons. */
        var controls = $('.carousel__controls', carousel)
        if (controls) controls.style.visibility = max <= 1 ? 'hidden' : 'visible'

        if (bar) {
          var visible = track.clientWidth / track.scrollWidth
          var progress = max > 0 ? track.scrollLeft / max : 0
          bar.style.width = Math.max(12, visible * 100) + '%'
          bar.style.transform = 'translateX(' + progress * ((1 / Math.max(visible, 0.12)) - 1) * 100 + '%)'
        }
      }

      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -page(), behavior: 'smooth' }) })
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: page(), behavior: 'smooth' }) })

      var ticking = false
      track.addEventListener('scroll', function () {
        if (ticking) return
        ticking = true
        window.requestAnimationFrame(function () {
          update()
          ticking = false
        })
      })

      window.addEventListener('resize', update)
      update()
    })
  }

  /* ───────────────────────────── 8. Sticky buy ────────────────────────── */

  function initStickyBuy() {
    var bar = $('[data-sticky-buy]')
    var anchor = $('[data-buy-anchor]')
    if (!bar || !anchor || typeof IntersectionObserver !== 'function') return

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.setAttribute('data-open', entry.isIntersecting ? 'false' : 'true')
        })
      },
      { threshold: 0 },
    )
    observer.observe(anchor)
  }

  /* ─────────────────────────────── Wiring ─────────────────────────────── */

  document.addEventListener('click', function (event) {
    var target = event.target

    var navToggle = target.closest('[data-nav-toggle]')
    if (navToggle) {
      var panel = document.getElementById('mobile-nav')
      if (!panel) return
      var isOpen = navToggle.getAttribute('aria-expanded') === 'true'
      navToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true')
      navToggle.textContent = isOpen
        ? navToggle.getAttribute('data-label-open') || 'Menu'
        : navToggle.getAttribute('data-label-close') || 'Close'
      panel.hidden = isOpen
      return
    }

    if (target.closest('[data-cart-open]')) {
      /* Only hijack the link when the drawer exists. Otherwise let the href
         through to /cart, which is the whole point of it being a link. */
      if (drawer) {
        event.preventDefault()
        openDrawer()
      }
      return
    }

    if (target.closest('[data-search-open]')) {
      openSearch()
      return
    }

    if (target.closest('[data-drawer-close]')) { closeDrawer(); return }
    if (target.closest('[data-search-close]')) { closeSearch(); return }

    if (target.closest('[data-overlay]')) {
      closeDrawer()
      closeSearch()
      return
    }

    var line = target.closest('[data-line]')
    if (line) {
      var index = parseInt(line.getAttribute('data-line'), 10)
      var current = parseInt(($('[data-qty-value]', line) || {}).textContent || '1', 10)
      if (target.closest('[data-qty-up]')) { changeLine(index, current + 1); return }
      if (target.closest('[data-qty-down]')) { changeLine(index, Math.max(0, current - 1)); return }
      if (target.closest('[data-line-remove]')) { changeLine(index, 0); return }
    }
  })

  document.addEventListener('submit', function (event) {
    var form = event.target
    if (form.matches('form[action*="/cart/add"]')) {
      event.preventDefault()
      handleProductSubmit(form)
    }
  })

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeDrawer()
      closeSearch()
      closeNav()
      return
    }

    if (drawer && drawer.getAttribute('data-open') === 'true') {
      trapFocus(drawer, event)
      return
    }

    if (searchPanel && searchPanel.getAttribute('data-open') === 'true') {
      if (event.key === 'ArrowDown') { event.preventDefault(); moveActive(1); return }
      if (event.key === 'ArrowUp') { event.preventDefault(); moveActive(-1); return }
      if (event.key === 'Enter' && activeIndex >= 0) {
        var hits = $$('.search-hit', searchResults)
        if (hits[activeIndex]) { event.preventDefault(); hits[activeIndex].click(); return }
      }
      trapFocus(searchPanel, event)
    }
  })

  if (searchInput) {
    searchInput.addEventListener('input', function () {
      clearTimeout(searchTimer)
      var term = searchInput.value.trim()
      /* Debounced so a fast typist does not fire a request per keystroke. */
      searchTimer = setTimeout(function () { runSearch(term) }, 200)
    })
  }

  /* ──────────────────────── 8. Header condense ────────────────────────── */

  /**
   * Shrinks the header once the page has scrolled past the top.
   *
   * The Q mark in the header is deliberately large, which is right at the top
   * of a page and wrong as a sticky bar that then covers a third of a phone
   * screen. A sentinel element at the very top of the document tells us when
   * we have left the top, and `data-condensed` on <html> does the rest in CSS
   * — no scroll handler, so nothing runs on every frame.
   */
  function initHeaderCondense() {
    var header = $('.site-header')
    if (!header || typeof IntersectionObserver === 'undefined') return

    var sentinel = $('[data-header-sentinel]')
    if (!sentinel) {
      sentinel = document.createElement('div')
      sentinel.setAttribute('data-header-sentinel', '')
      sentinel.setAttribute('aria-hidden', 'true')
      /* 80px tall rather than 1px: the header should not condense the
         instant the page moves, only once it has genuinely been scrolled. */
      sentinel.style.cssText =
        'position:absolute;top:0;left:0;width:1px;height:80px;pointer-events:none;'
      document.body.insertBefore(sentinel, document.body.firstChild)
    }

    new IntersectionObserver(function (entries) {
      root.toggleAttribute('data-condensed', !entries[0].isIntersecting)
    }).observe(sentinel)
  }

  /* The theme editor rebuilds sections in place; re-run the bits that bind
     to elements rather than to document. */
  document.addEventListener('shopify:section:load', function () {
    initReveal()
    initStickyBuy()
    initCarousels()
    initHeaderCondense()
  })

  initReveal()
  initStickyBuy()
  initCarousels()
  initHeaderCondense()
})()
